import React from 'react';
import { ModelRef, PromptTemplate } from '../../../api/structs';
import { REVIEW_MODES } from '../../../api/settings';
import { AIModel } from '../../../api/structs/model';
import ProviderModelPicker, {
  firstModelFor,
  modelOptionsFor,
  providerOptionsFor,
} from '../../ui/ProviderModelPicker';
import {
  InheritCaption,
  InheritedStatus,
  fieldCaption,
  fieldLabel,
  groupHeading,
} from './reviewFormStyles';

/** The gate values the engine treats as meaningful steps, not a free number. */
export const CONFIDENCE_GATES = [0.75, 0.85, 0.95];
export const SEVERITY_GATES = ['blocking', 'any'];
/**
 * 1..3 rather than 0..3 on purpose. `max_attempts` is `omitempty` on the wire
 * and `applyReviewGates` only overrides when the value is > 0, so a 0 written
 * at the pipeline or checkpoint layer is indistinguishable from "said nothing"
 * — the control would silently do nothing. Zero ("never regenerate") is a real
 * choice, but only the account layer, which always sends its value, can make
 * it; see the caption under this control.
 */
export const MAX_ATTEMPT_CHOICES = [1, 2, 3];

/** The non-capability review knobs; which of them a layer carries is its own. */
export interface ReviewTierFields {
  mode?: string;
  check?: ModelRef;
  confirm?: ModelRef;
  pair?: ModelRef;
  max_attempts?: number;
  confidence_gate?: number;
  severity_gate?: string;
  judge_template_id?: string;
}

/** What the layers ABOVE resolve each judge tier to, for the captions. */
export interface ResolvedReviewTiers {
  check: ModelRef;
  confirm: ModelRef;
  /** `{}` when no layer named one — it then follows the resolved confirm. */
  pair: ModelRef;
}

export interface ReviewModelsAndGatesProps {
  value: ReviewTierFields;
  inheritedTiers: ResolvedReviewTiers;
  inheritsFrom: string;
  models: AIModel[];
  /** `mode` belongs to the template layer; `judge_template_id` to a checkpoint. */
  showMode: boolean;
  showJudgeTemplate: boolean;
  showPair: boolean;
  /**
   * Whether this layer carries the check/confirm tiers and the gates at all.
   * Settings → Reviewer edits them in its own controls above, so the account
   * layer shows only `pair` — the one model that IS a capability field.
   */
  showCheckConfirm: boolean;
  /** Whether `inheritedTiers` is actually known yet — see `InheritCaption`. */
  inheritedStatus?: InheritedStatus;
  /** Per-instance prefix for the DOM ids; the inspector mounts twice. */
  idPrefix: string;
  promptTemplates: PromptTemplate[];
  onChange: (next: ReviewTierFields) => void;
}

const tierLabel: Record<'check' | 'confirm' | 'pair', string> = {
  check: 'Check model (cheap, judges everything)',
  confirm: 'Confirm model (strong, adjudicates fails)',
  pair: 'Frame-pair model',
};

const describeRef = (ref: ModelRef): string =>
  ref.provider || ref.model ? `${ref.provider || '—'} · ${ref.model || '—'}` : '—';

/**
 * The judge MODELS, the gates that decide when a fail costs anything, and (on
 * a checkpoint) which judge template does the reading.
 *
 * These are not capabilities — they do not change what the judge sees — but
 * they live on the same three layers and inherit the same way, so they are
 * edited here rather than in a fourth place.
 */
const ReviewModelsAndGates: React.FC<ReviewModelsAndGatesProps> = ({
  value,
  inheritedTiers,
  inheritsFrom,
  models,
  showMode,
  showJudgeTemplate,
  showPair,
  showCheckConfirm,
  inheritedStatus,
  idPrefix,
  promptTemplates,
  onChange,
}) => {
  const set = <K extends keyof ReviewTierFields>(key: K, next: ReviewTierFields[K]) =>
    onChange({ ...value, [key]: next });

  const unset = (key: keyof ReviewTierFields) => {
    const next = { ...value };
    delete next[key];
    onChange(next);
  };

  const modelRow = (tier: 'check' | 'confirm' | 'pair') => {
    const ref = value[tier] || {};
    const provider = ref.provider || '';
    const model = ref.model || '';
    // The engine fills whichever half of the pair judge no layer named from the
    // RESOLVED confirm tier (resolveReviewPair), so that is what the caption
    // has to promise — not an em-dash.
    const inheritedRef =
      tier === 'pair'
        ? {
            provider: inheritedTiers.pair.provider || inheritedTiers.confirm.provider,
            model: inheritedTiers.pair.model || inheritedTiers.confirm.model,
          }
        : inheritedTiers[tier];
    return (
      <div key={tier}>
        <h5 className={`${groupHeading} mb-1`}>{tierLabel[tier]}</h5>
        <ProviderModelPicker
          providerOptions={providerOptionsFor(models, provider)}
          provider={provider}
          onProviderChange={(next) =>
            set(tier, { provider: next, model: firstModelFor(models, next) })
          }
          providerAriaLabel={`${tier} model provider`}
          modelOptions={modelOptionsFor(models, provider, model)}
          model={model}
          onModelChange={(next) => set(tier, { provider, model: next })}
          modelAriaLabel={`${tier} model`}
        />
        <InheritCaption
          field={`model_${tier}`}
          from={inheritsFrom}
          status={inheritedStatus}
          inherits={describeRef(inheritedRef)}
          isSet={value[tier] !== undefined}
          onReset={() => unset(tier)}
        />
      </div>
    );
  };

  // A judge template is bound by id, and only a `judge-` template can answer a
  // judge prompt — offering the whole library would invite a silent mis-bind.
  const judgeTemplates = promptTemplates.filter((template) => template.id.startsWith('judge-'));

  return (
    <div className="space-y-3">
      {showMode && (
        <div>
          <label className={fieldLabel} htmlFor={`${idPrefix}-mode`}>
            Review mode
          </label>
          <select
            id={`${idPrefix}-mode`}
            data-testid="review-mode"
            className="select w-full"
            value={value.mode ?? ''}
            onChange={(event) => {
              if (event.target.value === '') unset('mode');
              else set('mode', event.target.value);
            }}
          >
            <option value="">inherit from your account</option>
            {REVIEW_MODES.map((mode) => (
              <option key={mode} value={mode}>
                {mode}
              </option>
            ))}
          </select>
          <p className={fieldCaption}>
            off: judge and badge only. on: repair confirmed blocking fails. strict: any confirmed
            fail blocks the run.
          </p>
        </div>
      )}

      {showCheckConfirm && modelRow('check')}
      {showCheckConfirm && modelRow('confirm')}
      {showPair && modelRow('pair')}

      {showCheckConfirm && (
      <div className="flex flex-wrap gap-3">
        <div className="w-32">
          <label className={fieldLabel} htmlFor={`${idPrefix}-confidence-gate`}>
            Confidence gate
          </label>
          <select
            id={`${idPrefix}-confidence-gate`}
            data-testid="review-confidence-gate"
            className="select w-full"
            value={value.confidence_gate === undefined ? '' : String(value.confidence_gate)}
            onChange={(event) => {
              if (event.target.value === '') unset('confidence_gate');
              else set('confidence_gate', Number(event.target.value));
            }}
          >
            <option value="">inherit</option>
            {CONFIDENCE_GATES.map((gate) => (
              <option key={gate} value={gate}>
                {gate}
              </option>
            ))}
          </select>
        </div>

        <div className="w-32">
          <label className={fieldLabel} htmlFor={`${idPrefix}-severity-gate`}>
            Severity gate
          </label>
          <select
            id={`${idPrefix}-severity-gate`}
            data-testid="review-severity-gate"
            className="select w-full"
            value={value.severity_gate ?? ''}
            onChange={(event) => {
              if (event.target.value === '') unset('severity_gate');
              else set('severity_gate', event.target.value);
            }}
          >
            <option value="">inherit</option>
            {SEVERITY_GATES.map((gate) => (
              <option key={gate} value={gate}>
                {gate}
              </option>
            ))}
          </select>
        </div>

        <div className="w-32">
          <label className={fieldLabel} htmlFor={`${idPrefix}-max-attempts`}>
            Max attempts
          </label>
          <select
            id={`${idPrefix}-max-attempts`}
            data-testid="review-max-attempts"
            className="select w-full"
            value={value.max_attempts === undefined ? '' : String(value.max_attempts)}
            onChange={(event) => {
              if (event.target.value === '') unset('max_attempts');
              else set('max_attempts', Number(event.target.value));
            }}
          >
            <option value="">inherit</option>
            {/* A template stored before this rule can hold a 0. Show it rather
                than rendering a select whose value matches no option — but do
                not offer it as a choice, because writing it does nothing. */}
            {value.max_attempts === 0 && (
              <option value="0" disabled>
                0 (stored, but inherited on the wire)
              </option>
            )}
            {MAX_ATTEMPT_CHOICES.map((attempts) => (
              <option key={attempts} value={attempts}>
                {attempts}
              </option>
            ))}
          </select>
          <p className={fieldCaption} data-testid="review-max-attempts-note">
            0 (never regenerate) is only settable in Settings → Reviewer — here it would be
            indistinguishable from “inherit”.
          </p>
        </div>
      </div>
      )}

      {showJudgeTemplate && (
        <div>
          <label className={fieldLabel} htmlFor={`${idPrefix}-judge-template`}>
            Judge template
          </label>
          <select
            id={`${idPrefix}-judge-template`}
            data-testid="review-judge-template"
            className="select w-full"
            value={value.judge_template_id ?? ''}
            onChange={(event) => {
              if (event.target.value === '') unset('judge_template_id');
              else set('judge_template_id', event.target.value);
            }}
          >
            <option value="">the built-in judge for this checkpoint</option>
            {judgeTemplates.map((template) => (
              <option key={template.id} value={template.id}>
                {template.name || template.id}
              </option>
            ))}
          </select>
          <p className={fieldCaption}>
            Only templates whose id starts with <code>judge-</code> are offered. Edit them in Prompt
            templates.
          </p>
        </div>
      )}
    </div>
  );
};

export default ReviewModelsAndGates;
